'use client'

import Link from 'next/link'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { CloseIcon } from '@/components/icons/CloseIcon'
import { cn } from '@/utilities/ui'
import { Wordmark } from './Wordmark'
import { AsciiField } from './AsciiField'
import { MenuIcon } from './icons'
import type { UnveilingSection } from './paths'
import { DISPLAY, DISPLAY_XXS, MONO_KICKER, MONO_MICRO } from './tokens'

type Item = { key: UnveilingSection; label: string; href: string }

type Props = {
  items: Item[]
  active: UnveilingSection
}

export function MobileMenu({ items, active }: Props) {
  const [open, setOpen] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  // The panel unmounts on close, taking focus with it. A nav link is left alone: focus belongs to
  // the page being navigated to, not back on the button that opened the menu.
  const close = useCallback(() => {
    setOpen(false)
    trigger.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
        return
      }
      // The page behind the takeover is still in the tab order, so Tab wraps by hand.
      if (event.key !== 'Tab' || !panel.current) return
      const stops = [...panel.current.querySelectorAll<HTMLElement>('a[href], button')]
      if (!stops.length) return
      const first = stops[0]
      const last = stops[stops.length - 1]
      if (document.activeElement !== (event.shiftKey ? first : last)) return
      event.preventDefault()
      ;(event.shiftKey ? last : first).focus()
    }
    document.addEventListener('keydown', onKeyDown)
    // Rotating a tablet past lg hides the overlay via CSS, so close it or the scroll lock strands the page.
    const desktop = window.matchMedia('(min-width: 1024px)')
    const onDesktop = (event: MediaQueryListEvent) => event.matches && setOpen(false)
    desktop.addEventListener('change', onDesktop)
    // The takeover covers the viewport, so the page behind it must not scroll under it.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      desktop.removeEventListener('change', onDesktop)
      document.body.style.overflow = previous
    }
  }, [close, open])

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-label="Open menu"
        className={cn(
          'flex size-[38px] shrink-0 items-center justify-center rounded-[2px] border border-civo-cloud/25 bg-[#0a0c0d] text-civo-white lg:hidden',
          'transition-colors duration-200 ease-out hover:border-civo-white/50 motion-reduce:transition-none',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-civo-lime',
        )}
      >
        <MenuIcon className="size-4" />
      </button>

      {open ? (
        <div
          ref={panel}
          role="dialog"
          aria-modal
          aria-label="Event sections"
          className="fixed inset-0 z-[60] overflow-hidden bg-[#020304] animate-[vr-fade-in_200ms_ease-out] lg:hidden motion-reduce:animate-none"
        >
          <AsciiField className="pointer-events-none absolute inset-0 size-full select-none opacity-[0.32]" />

          <div className="relative flex h-full flex-col justify-between px-7 pb-8">
            <div className="flex items-center justify-between py-[29px]">
              <Wordmark />
              <button
                type="button"
                onClick={close}
                autoFocus
                className={cn(
                  MONO_KICKER,
                  'flex items-center gap-2 text-civo-cloud/60 transition-colors duration-200 ease-out hover:text-civo-white motion-reduce:transition-none',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-civo-lime',
                )}
              >
                Close
                <CloseIcon className="size-3" />
              </button>
            </div>

            <nav className="flex flex-col">
              {items.map(({ key, label, href }, i) => {
                const isActive = key === active
                return (
                  <Link
                    key={key}
                    href={href}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setOpen(false)}
                    className="group/item flex flex-col gap-2.5 border-t border-civo-cloud/25 py-6"
                  >
                    <span
                      className={cn(
                        MONO_KICKER,
                        isActive ? 'text-civo-lime' : 'text-civo-cloud/40',
                      )}
                    >
                      {`/// 0${i + 1}`}
                    </span>
                    <span
                      className={cn(
                        DISPLAY,
                        DISPLAY_XXS,
                        'transition-colors duration-200 ease-out motion-reduce:transition-none',
                        isActive
                          ? 'text-civo-lime'
                          : 'text-civo-white group-hover/item:text-civo-lime',
                      )}
                    >
                      {label}
                    </span>
                  </Link>
                )
              })}
            </nav>

            <p className={cn(MONO_MICRO, 'text-right text-civo-cloud/40')}>Vera Rubin NVL72</p>
          </div>
        </div>
      ) : null}
    </>
  )
}
